<pt-feature-info />
<pt-breadcrumbs class="bg-white" [breadcrumbs]="breadcrumbs$ | async" />
<div class="border-b border-solid border-slate-300"></div>

<form [formGroup]="form" (ngSubmit)="submit()">
  <div class="mx-auto flex max-w-5xl flex-col gap-4 p-4">
    <pr-content-container>
      <div class="flex flex-col gap-4">
        <h2 class="text-xl font-bold">Provider Settings</h2>

        <div *prIsRegion="[Region.NewZealand]" class="flex flex-col gap-2">
          <p class="text-md font-bold">MOH Provider Settings</p>

          <div formGroupName="nzProviderDetails" class="flex gap-2">
            <mat-form-field class="flex-1">
              <mat-label>DCNZ Number</mat-label>
              <input matInput formControlName="dcnzNumber" />
              @if (nzProviderDetails.controls.dcnzNumber.invalid) {
                <mat-error>{{ nzMaxLengthError }}</mat-error>
              }
            </mat-form-field>
          </div>

          <div
            formArrayName="nzPracticeProviderDetails"
            class="flex flex-col gap-2"
          >
            @if (practices$ | async; as practices) {
              @if (selectablePracticesByRow$ | async; as practiceOptions) {
                @for (
                  control of nzPracticeProviderDetails.controls;
                  track $index;
                  let isFirst = $first;
                  let isLast = $last
                ) {
                  <div class="flex items-center gap-2" [formGroupName]="$index">
                    <mat-form-field class="flex-1">
                      <mat-label>Practice</mat-label>
                      <mat-select
                        formControlName="practiceRef"
                        [compareWith]="isSelectedPracticeRef"
                        [errorStateMatcher]="practiceRequiredMatcher"
                      >
                        @for (
                          practice of practiceOptions[$index];
                          track practice.ref.path
                        ) {
                          <mat-option [value]="practice.ref">
                            {{ practice.name }}
                          </mat-option>
                        }
                      </mat-select>
                      <mat-error>Select a practice for these numbers</mat-error>
                    </mat-form-field>

                    <mat-form-field class="flex-1">
                      <mat-label>Payee Number</mat-label>
                      <input matInput formControlName="payeeNumber" />
                      <mat-error>{{ nzMaxLengthError }}</mat-error>
                    </mat-form-field>

                    <mat-form-field class="flex-1">
                      <mat-label>Agreement Number</mat-label>
                      <input matInput formControlName="agreementNumber" />
                      <mat-error>{{ nzMaxLengthError }}</mat-error>
                    </mat-form-field>

                    @if (practices.length > 1) {
                      <button
                        type="button"
                        mat-icon-button
                        matTooltip="Remove practice numbers"
                        [disabled]="isFirst"
                        (click)="
                          nzPracticeProviderDetailControls.removeControl($index)
                        "
                      >
                        <mat-icon>remove</mat-icon>
                      </button>
                      <button
                        type="button"
                        mat-icon-button
                        matTooltip="Add practice numbers"
                        [disabled]="!isLast || practices.length <= $index + 1"
                        (click)="
                          nzPracticeProviderDetailControls.addControl($index)
                        "
                      >
                        <mat-icon>add</mat-icon>
                      </button>
                    }
                  </div>
                }
              }
            }
          </div>
        </div>

        <div
          *prIsRegion="[Region.NewZealand]"
          formGroupName="nzProviderDetails"
        >
          <div formGroupName="acc" class="flex flex-col gap-2">
            <p class="text-md font-bold">ACC Provider Settings</p>

            <div class="flex gap-2">
              <mat-form-field class="flex-1">
                <mat-label>ACC Provider Id</mat-label>
                <input matInput formControlName="providerId" />
                @if (accProviderDetails.controls.providerId.invalid) {
                  <mat-error>{{ nzMaxLengthError }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>Provider Type</mat-label>
                <mat-select formControlName="providerTypeCode" required>
                  @for (option of providerTypeOptions; track option.code) {
                    <mat-option [value]="option.code">
                      {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
                @if (accProviderDetails.controls.providerTypeCode.invalid) {
                  <mat-error>This field is required</mat-error>
                }
              </mat-form-field>
            </div>
          </div>
        </div>

        <div *prIsRegion="[Region.Australia]" formArrayName="providerDetails">
          <p class="text-md font-bold">Healthcare Claim Details</p>
          <pr-provider-details-input
            *ngFor="
              let control of providerDetails.controls;
              trackBy: trackByIndex;
              index as index;
              first as isFirst;
              last as isLast
            "
            [form]="control"
          >
            <div class="flex gap-2">
              <button
                mat-icon-button
                matTooltip="Remove provider option"
                [disabled]="isFirst"
                (click)="providerDetailControls.removeControl(index)"
              >
                <mat-icon>remove</mat-icon>
              </button>
              <button
                *ngIf="practices$ | async as practices"
                mat-icon-button
                matTooltip="Add new provider option"
                [disabled]="!isLast || practices.length <= index + 1"
                (click)="providerDetailControls.addControl(index)"
              >
                <mat-icon>add</mat-icon>
              </button>
            </div>
          </pr-provider-details-input>
        </div>
        <div class="flex flex-col gap-2">
          <p class="text-md font-bold">Prescription Prescriber Number</p>
          <mat-form-field>
            <mat-label>Prescriber Number</mat-label>
            <input matInput formControlName="prescriberNumber" />
          </mat-form-field>
        </div>
        <div class="flex flex-col gap-2">
          <p class="text-md font-bold">Qualifications</p>
          <mat-form-field>
            <mat-label>Qualifications</mat-label>
            <input matInput formControlName="qualifications" />
            <mat-hint>
              Separate multiple qualifications with a comma e.g. MBBS, FRACGP
            </mat-hint>
          </mat-form-field>
        </div>

        <div *ptFeatureEnabled="aiNotesFeature" class="flex flex-col gap-2">
          <p class="text-md font-bold">Heidi Health</p>
          <div class="flex items-center gap-4" formGroupName="heidi">
            <mat-form-field class="flex-1">
              <mat-label>Account Email</mat-label>
              <input matInput formControlName="email" />
              <mat-hint>
                Enter the email address associated with your Heidi Health
                account
              </mat-hint>
            </mat-form-field>
            <div class="flex flex-1 flex-col">
              <mat-checkbox
                *ngrxLet="staffer$ as staffer"
                formControlName="useAsDefault"
              >
                Use this account by default
              </mat-checkbox>
              <span class="ml-2 text-xs opacity-70">
                <mat-icon class="icon-xs mr-1">info</mat-icon>
                Skips the account selection step by preferring your account over
                the practice's
              </span>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <p class="text-md font-bold">Other Details</p>
          <mat-form-field>
            <mat-label>Provider Commission</mat-label>
            <input
              matInput
              type="number"
              min="0"
              max="100"
              formControlName="providerCommission"
            />
            <span matTextSuffix>%</span>
          </mat-form-field>
        </div>
      </div>
    </pr-content-container>

    @if (billingEntitiesEnabled$ | async) {
      <pr-content-container>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold">Billing Entities</h2>
            <button
              mat-flat-button
              matTooltip="Add new Billing Entity"
              color="primary"
              (click)="billingEntityControls.addControl()"
            >
              Add Billing Entity
            </button>
          </div>

          <div class="flex flex-col gap-2">
            @if (!billingEntities.controls.length) {
              <pr-empty-state type="text" title="billing entities" />
            }

            @for (
              control of billingEntities.controls;
              track $index;
              let index = $index;
              let isFirst = $first
            ) {
              <pr-billing-entity-input [form]="control">
                <div class="flex gap-2">
                  @if (!isFirst) {
                    <button
                      mat-stroked-button
                      matTooltip="Remove Billing Entity"
                      color="warn"
                      (click)="billingEntityControls.removeControl(index)"
                    >
                      Remove
                    </button>
                  }
                </div>
              </pr-billing-entity-input>
            }
          </div>
        </div>
      </pr-content-container>
    }

    <pr-content-container>
      <h2 class="text-xl font-bold">Online Profile Settings</h2>

      <div class="flex flex-col gap-4" formGroupName="onlineProfile">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-6">
          <div class="flex flex-col gap-4 md:col-span-3 md:flex-row">
            <pt-profile-image-manager
              [storagePath]="storagePath$ | async"
              [profile]="profile$ | async"
              [diameter]="46"
              (storagePathUrlChange)="updateImageUrl($event)"
            />
            <mat-form-field class="w-full">
              <mat-label>Name</mat-label>

              <input
                matInput
                formControlName="name"
                [placeholder]="(profile$ | async)?.name"
              />
              <mat-hint> Defaults to your user name if left blank. </mat-hint>
            </mat-form-field>
          </div>

          <mat-form-field class="w-full md:col-span-2">
            <mat-label>Title</mat-label>
            <input matInput formControlName="title" />
            <mat-hint> e.g Dentist, Orthodontist, Dental Hygienist </mat-hint>
          </mat-form-field>

          <mat-form-field class="w-full md:col-span-1">
            <mat-label>Gender</mat-label>
            <mat-select formControlName="gender">
              @for (gender of genders; track $index) {
                <mat-option [value]="gender">
                  {{ gender | splitCamel | titlecase }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field class="w-full">
          <mat-label>Bio</mat-label>
          <textarea matInput formControlName="bio"></textarea>
          <mat-hint>
            A short bio about the provider that will be displayed on their
            online profile.
          </mat-hint>
        </mat-form-field>
      </div>
    </pr-content-container>

    <pr-content-container margin="mb-2" class="sticky bottom-0 z-50">
      <div class="flex items-center justify-end">
        <button mat-flat-button color="primary" [disabled]="form.invalid">
          Save
        </button>
      </div>
    </pr-content-container>
  </div>
</form>
